// src/components/patients/PatientModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  Box,
  Chip,
  FormHelperText,
  IconButton,
  Alert,
  CircularProgress,
  RadioGroup,
  Radio,
  Tooltip
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, Info as InfoIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import agthealthService from '../../../services/agthealthService';

// Color verde definido
const appleGreen = '#4CAF50';

// Estilo mejorado para el Dialog con efecto blur al estilo Apple
const StyledDialog = styled(Dialog)(({ theme }) => ({
  // Configuración para el backdrop con blur
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(3px)',
    backgroundColor: 'transparent',
    transition: 'backdrop-filter 0.3s, background-color 0.3s'
  },
  // Animación para el papel del diálogo
  '& .MuiDialog-paper': {
    borderRadius: 16,
    maxWidth: '1200px',
    width: '100%',
    boxShadow: '0 12px 28px rgba(0,0,0,0.12), 0 8px 12px rgba(0,0,0,0.08)',
    border: '1px solid rgba(255,255,255,0.3)',
    animation: `$dialogFadeIn 0.4s ${theme.transitions.easing.easeOut}`,
    overflow: 'hidden'
  },
  // Keyframe para la animación de entrada
  '@keyframes dialogFadeIn': {
    '0%': {
      opacity: 0,
      transform: 'scale(0.97)'
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1)'
    }
  }
}));

// Estilo para los inputs al estilo Apple con mayor altura
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    minHeight: '56px', // Aumentar la altura del input
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px rgba(76, 175, 80, 0.2)`,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: appleGreen,
        borderWidth: 1
      }
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: appleGreen
    },
    '& .MuiInputBase-input': {
      fontSize: '1rem', // Aumentar tamaño de texto
      padding: '14px 16px', // Aumentar el padding interno
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '1rem', // Aumentar tamaño de texto de las etiquetas
    transform: 'translate(16px, 16px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(16px, -6px) scale(0.75)',
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: appleGreen
  },
  // Para inputs multilinea
  '& .MuiOutlinedInput-multiline': {
    height: 'auto',
  },
  // Para inputs con tipo date
  '& .MuiOutlinedInput-inputAdornedEnd': {
    paddingRight: '10px',
  }
}));

// Estilo para los checkboxes al estilo Apple
const StyledCheckbox = styled(Checkbox)({
  color: 'rgba(0, 0, 0, 0.54)',
  '&.Mui-checked': {
    color: appleGreen
  }
});

// Estilo para los radio buttons al estilo Apple
const StyledRadio = styled(Radio)({
  color: 'rgba(0, 0, 0, 0.54)',
  '&.Mui-checked': {
    color: appleGreen
  }
});

// Estilo para los chips al estilo Apple
const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(76, 175, 80, 0.2)' 
    : 'rgba(76, 175, 80, 0.1)',
  border: '1px solid rgba(76, 175, 80, 0.3)',
  color: appleGreen,
  '& .MuiChip-deleteIcon': {
    color: appleGreen,
    '&:hover': {
      color: theme.palette.mode === 'dark' 
        ? '#fff' 
        : 'rgba(76, 175, 80, 0.8)'
    }
  }
}));

// Estilo para los botones al estilo Apple
const StyledButton = styled(Button)({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: 'none',
  padding: '8px 16px',
  '&.MuiButton-contained': {
    backgroundColor: appleGreen,
    '&:hover': {
      backgroundColor: '#43A047',
      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
    }
  },
  '&.MuiButton-text': {
    color: appleGreen,
    '&:hover': {
      backgroundColor: 'rgba(76, 175, 80, 0.08)'
    }
  }
});

// Estilo para los iconos de botón al estilo Apple
const StyledIconButton = styled(IconButton)({
  color: appleGreen,
  '&:hover': {
    backgroundColor: 'rgba(76, 175, 80, 0.08)'
  }
});

// Estilo para los encabezados
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700, // Más grueso
  color: '#333',
  marginBottom: theme.spacing(2),
}));

// Estilo para los textos de ayuda
const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  fontSize: '0.9rem', // Aumentar tamaño de texto de ayuda
  margin: '4px 0 0 8px',
  color: theme.palette.text.secondary
}));

// Estilo para las etiquetas de campo
const FieldLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#555',
  marginBottom: theme.spacing(1),
  fontSize: '0.9rem',
}));

// Función para determinar el estado del IMC
const getBmiStatus = (bmi) => {
  const numBmi = parseFloat(bmi);
  if (isNaN(numBmi)) return { color: 'text.secondary', message: 'Sin datos' };
  
  if (numBmi < 18.5) return { color: '#FFC107', message: 'Bajo peso' }; // Amarillo
  if (numBmi >= 18.5 && numBmi < 25) return { color: '#4CAF50', message: 'Normal' }; // Verde
  if (numBmi >= 25 && numBmi < 30) return { color: '#FF9800', message: 'Sobrepeso' }; // Naranja
  if (numBmi >= 30) return { color: '#F44336', message: 'Obesidad' }; // Rojo
  
  return { color: 'text.secondary', message: 'Sin datos' };
};

const PatientModal = ({ open, onClose, patientId = null, onSaved }) => {
  const [loading, setLoading] = useState(!!patientId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const isEditMode = !!patientId;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    identificationNumber: '',
    countryCode: 'cl',
    phoneNumber: '',
    email: '',
    birthDate: '',
    age: '',
    gender: '',
    weight: '',
    height: '',
    bmi: '',
    bloodType: '',
    chronicDiseases: [],
    cancer: false,
    allergies: [],
    medications: [],
    notes: '',
    // Nuevos campos
    systolicBP: '',
    diastolicBP: '',
    smoking: false,
    alcohol: false,
    waistCircumference: '',
    oxygenSaturation: '',
    temperature: '',
    bloodGlucose: '',
    heartRate: '',
    diabetesType: '',
    currentPathologies: []
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  // Calcular edad automáticamente basado en la fecha de nacimiento
  useEffect(() => {
    if (formData.birthDate) {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      const currentYear = 2025; // Año actual fijo en 2025
   
      // Crear una fecha con el año actual pero manteniendo mes y día del nacimiento
      const birthDateThisYear = new Date(currentYear, birthDate.getMonth(), birthDate.getDate());
   
      // Calcular edad básica (diferencia de años)
      let age = currentYear - birthDate.getFullYear();
   
      // Si aún no ha pasado el cumpleaños este año, restar 1
      if (today < birthDateThisYear) {
        age--;
      }
   
      // Siempre actualizar la edad calculada
      if (age >= 0) {
        setFormData(prev => ({ ...prev, age: age.toString() }));
      }
    }
  }, [formData.birthDate]); // Solo dependemos de birthDate
  
  // Calcular IMC cuando cambia el peso o la altura
  useEffect(() => {
    if (formData.weight && formData.height) {
      const heightInMeters = Number(formData.height) / 100;
      const weightInKg = Number(formData.weight);
      if (heightInMeters > 0 && weightInKg > 0) {
        const bmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
        setFormData(prev => ({ ...prev, bmi }));
      }
    } else {
      setFormData(prev => ({ ...prev, bmi: '' }));
    }
  }, [formData.weight, formData.height]);
  
  // Cargar datos del paciente si estamos en modo edición
  useEffect(() => {
    if (isEditMode && open) {
      const fetchPatient = async () => {
        try {
          setLoading(true);
          const response = await agthealthService.getPatientById(patientId);
          const patientData = response.data;
          
          setFormData({
            ...patientData,
            age: patientData.age?.toString() || '',
            weight: patientData.weight?.toString() || '',
            height: patientData.height?.toString() || '',
            bmi: patientData.bmi?.toString() || '',
            systolicBP: patientData.systolicBP?.toString() || '',
            diastolicBP: patientData.diastolicBP?.toString() || '',
            waistCircumference: patientData.waistCircumference?.toString() || '',
            oxygenSaturation: patientData.oxygenSaturation?.toString() || '',
            temperature: patientData.temperature?.toString() || '',
            bloodGlucose: patientData.bloodGlucose?.toString() || '',
            heartRate: patientData.heartRate?.toString() || '',
            chronicDiseases: patientData.chronicDiseases || [],
            allergies: patientData.allergies || [],
            medications: patientData.medications || [],
            currentPathologies: patientData.currentPathologies || []
          });
          
          setError(null);
        } catch (err) {
          console.error('Error al cargar datos del paciente:', err);
          setError('No se pudieron cargar los datos del paciente');
        } finally {
          setLoading(false);
        }
      };
      
      fetchPatient();
    }
  }, [patientId, isEditMode, open]);
  
  // Resetear el formulario cuando se abre/cierra
  useEffect(() => {
    if (!open) {
      // Si el modal se cierra, resetear estados
      setSuccess(false);
      setError(null);
      
      if (!isEditMode) {
        // Solo resetear el formulario si no estamos en modo edición
        setFormData({
          firstName: '',
          lastName: '',
          identificationNumber: '',
          countryCode: 'cl',
          phoneNumber: '',
          email: '',
          birthDate: '',
          age: '',
          gender: '',
          weight: '',
          height: '',
          bmi: '',
          bloodType: '',
          chronicDiseases: [],
          cancer: false,
          allergies: [],
          medications: [],
          notes: '',
          systolicBP: '',
          diastolicBP: '',
          smoking: false,
          alcohol: false,
          waistCircumference: '',
          oxygenSaturation: '',
          temperature: '',
          bloodGlucose: '',
          heartRate: '',
          diabetesType: '',
          currentPathologies: []
        });
        setFormErrors({});
      }
    }
  }, [open, isEditMode]);
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'El nombre es obligatorio';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'El apellido es obligatorio';
    }
    
    if (!formData.identificationNumber.trim()) {
      errors.identificationNumber = 'El número de identificación es obligatorio';
    }
    
    // Validar email si se proporciona
    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      errors.email = 'El formato del email no es válido';
    }
    
    // Validar campos numéricos
    const numericFields = [
      'age', 'weight', 'height', 'systolicBP', 'diastolicBP', 
      'waistCircumference', 'oxygenSaturation', 'temperature', 
      'bloodGlucose', 'heartRate'
    ];
    
    numericFields.forEach(field => {
      if (formData[field] && isNaN(Number(formData[field]))) {
        errors[field] = 'Este campo debe ser un número';
      }
    });
    
    // Validación adicional para rangos específicos
    if (formData.systolicBP && (Number(formData.systolicBP) < 50 || Number(formData.systolicBP) > 250)) {
      errors.systolicBP = 'La presión sistólica debe estar entre 50 y 250';
    }
    
    if (formData.diastolicBP && (Number(formData.diastolicBP) < 30 || Number(formData.diastolicBP) > 150)) {
      errors.diastolicBP = 'La presión diastólica debe estar entre 30 y 150';
    }
    
    if (formData.oxygenSaturation && (Number(formData.oxygenSaturation) < 50 || Number(formData.oxygenSaturation) > 100)) {
      errors.oxygenSaturation = 'La saturación debe estar entre 50% y 100%';
    }
    
    if (formData.temperature && (Number(formData.temperature) < 30 || Number(formData.temperature) > 45)) {
      errors.temperature = 'La temperatura debe estar entre 30°C y 45°C';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const handleArrayInput = (field, value) => {
    if (value.trim()) {
      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      return true;
    }
    return false;
  };
  
  const handleRemoveItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Convertir campos numéricos a números
      const numericFields = [
        'age', 'weight', 'height', 'bmi', 'systolicBP', 'diastolicBP',
        'waistCircumference', 'oxygenSaturation', 'temperature',
        'bloodGlucose', 'heartRate'
      ];
      
      const processedData = { ...formData };
      
      numericFields.forEach(field => {
        processedData[field] = formData[field] ? Number(formData[field]) : null;
      });
      
      if (isEditMode) {
        await agthealthService.updatePatient(patientId, processedData);
      } else {
        await agthealthService.createPatient(processedData);
      }
      
      setSuccess(true);
      
      // Notificar que se ha guardado correctamente
      if (onSaved) {
        onSaved();
      }
      
      // Cerrar el modal después de un breve retraso
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (err) {
      console.error('Error al guardar paciente:', err);
      setError(err.response?.data?.message || 'Error al guardar los datos del paciente');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <StyledDialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      aria-labelledby="patient-modal-title"
      // Añadimos transiciones más suaves
      TransitionProps={{
        timeout: 300
      }}
    >
      <DialogTitle id="patient-modal-title" sx={{ pb: 1, pt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
            {isEditMode ? 'Editar Paciente' : 'Nuevo Paciente'}
          </Typography>
          <StyledIconButton
            edge="end"
            onClick={onClose}
            disabled={saving}
            aria-label="close"
            size="large"
          >
            <CloseIcon />
          </StyledIconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ px: 3, py: 3, borderColor: 'rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: appleGreen }} />
          </Box>
        ) : (
          <form id="patient-form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  color: appleGreen,
                  '& .MuiAlert-icon': {
                    color: appleGreen
                  }
                }}
              >
                Paciente {isEditMode ? 'actualizado' : 'creado'} con éxito
              </Alert>
            )}
            
            <SectionTitle variant="h6">
              Información Personal
            </SectionTitle>
            
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <FieldLabel>
                  Nombre *
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Ingrese el nombre"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>
                  Apellido *
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Ingrese el apellido"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>
                  País *
                </FieldLabel>
                <Select
                  fullWidth
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  required
                  sx={{
                    borderRadius: 3,
                    height: '56px',
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }}
                >
                  <MenuItem value="cl">Chile</MenuItem>
                  <MenuItem value="us">Estados Unidos</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>
                  Número de Identificación (RUT/SSN) *
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Ingrese RUT sin puntos ni guión o SSN"
                  name="identificationNumber"
                  value={formData.identificationNumber}
                  onChange={handleInputChange}
                  error={!!formErrors.identificationNumber}
                  helperText={formErrors.identificationNumber}
                  required
                  disabled={isEditMode} // No permitir editar la identificación en modo edición
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>
                  Teléfono
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Ingrese número telefónico"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>
                  Email
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Ingrese correo electrónico"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.08)' }} />
            
            <SectionTitle variant="h6">
              Información Médica Básica
            </SectionTitle>
            
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  Fecha de Nacimiento
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Seleccione fecha"
                  name="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  Edad (calculada)
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Se calcula automáticamente"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleInputChange}
                  error={!!formErrors.age}
                  helperText={formErrors.age || "Calculada a partir de la fecha de nacimiento"}
                  InputProps={{ 
                    inputProps: { min: 0, max: 120 },
                    readOnly: !!formData.birthDate // Solo lectura si hay fecha de nacimiento
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  Género
                </FieldLabel>
                <Select
                  fullWidth
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  sx={{
                    borderRadius: 3,
                    height: '56px',
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { width: '250px' } // Haciendo el menú más ancho
                    }
                  }}
                >
                  <MenuItem value="male">Masculino</MenuItem>
                  <MenuItem value="female">Femenino</MenuItem>
                  <MenuItem value="other">Otro</MenuItem>
                  <MenuItem value="prefer_not_to_say">Prefiero no decir</MenuItem>
                </Select>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  Peso (kg)
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Ingrese peso en kg"
                  name="weight"
                  type="number"
                  value={formData.weight}
                  onChange={handleInputChange}
                  error={!!formErrors.weight}
                  helperText={formErrors.weight}
                  InputProps={{ inputProps: { min: 0, step: "0.1" } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  Altura (cm)
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Ingrese altura en cm"
                  name="height"
                  type="number"
                  value={formData.height}
                  onChange={handleInputChange}
                  error={!!formErrors.height}
                  helperText={formErrors.height}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  IMC
                </FieldLabel>
                <StyledTextField
                  fullWidth
                  placeholder="Calculado automáticamente"
                  name="bmi"
                  value={formData.bmi}
                  disabled
                  InputProps={{ 
                    endAdornment: formData.bmi && (
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        <Box 
                          sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%', 
                            bgcolor: getBmiStatus(formData.bmi).color,
                            mr: 1
                          }} 
                        />
                        <Typography variant="caption" sx={{ color: getBmiStatus(formData.bmi).color, fontWeight: 'bold' }}>
                          {getBmiStatus(formData.bmi).message}
                        </Typography>
                      </Box>
                    )
                  }}
                />
                {formData.bmi && (
                  <StyledFormHelperText sx={{ mx: 1 }}>
                    Valor: {formData.bmi} kg/m² - {getBmiStatus(formData.bmi).message}
                  </StyledFormHelperText>
                )}
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  Tipo de Sangre
                </FieldLabel>
                <Select
                  fullWidth
                  name="bloodType"
                  value={formData.bloodType}
                  onChange={handleInputChange}
                  sx={{
                    borderRadius: 3,
                    height: '56px',
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { width: '200px' } // Haciendo el menú más ancho
                    }
                  }}
                >
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                </Select>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FieldLabel>
                  Tipo de Diabetes
                </FieldLabel>
                <Select
                  fullWidth
                  name="diabetesType"
                  value={formData.diabetesType}
                  onChange={handleInputChange}
                  sx={{
                    borderRadius: 3,
                    height: '56px',
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }}
                >
                  <MenuItem value="">No tiene</MenuItem>
                  <MenuItem value="type1">Tipo 1</MenuItem>
                  <MenuItem value="type2">Tipo 2</MenuItem>
                  <MenuItem value="gestational">Gestacional</MenuItem>
                  <MenuItem value="prediabetes">Prediabetes</MenuItem>
                </Select>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.08)' }} />
            
            <SectionTitle variant="h6">
              Signos Vitales y Mediciones
            </SectionTitle>
            
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <FieldLabel>
                      Presión Sistólica
                    </FieldLabel>
                    <StyledTextField
                      fullWidth
                      placeholder="mmHg"
                      name="systolicBP"
                      type="number"
                      value={formData.systolicBP}
                      onChange={handleInputChange}
                      error={!!formErrors.systolicBP}
                      helperText={formErrors.systolicBP}
                      InputProps={{ 
                        inputProps: { min: 50, max: 250 },
                        endAdornment: <Typography variant="caption">mmHg</Typography>
                      }}
                    />
                  </Box>
                  <Tooltip title="Valor normal: 90-120 mmHg">
                    <InfoIcon color="action" sx={{ ml: 1, mt: 3 }} />
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <FieldLabel>
                      Presión Diastólica
                    </FieldLabel>
                    <StyledTextField
                      fullWidth
                      placeholder="mmHg"
                      name="diastolicBP"
                      type="number"
                      value={formData.diastolicBP}
                      onChange={handleInputChange}
                      error={!!formErrors.diastolicBP}
                      helperText={formErrors.diastolicBP}
                      InputProps={{ 
                        inputProps: { min: 30, max: 150 },
                        endAdornment: <Typography variant="caption">mmHg</Typography>
                      }}
                    />
                  </Box>
                  <Tooltip title="Valor normal: 60-80 mmHg">
                    <InfoIcon color="action" sx={{ ml: 1, mt: 3 }} />
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <FieldLabel>
                      Frecuencia Cardíaca
                    </FieldLabel>
                    <StyledTextField
                      fullWidth
                      placeholder="lpm"
                      name="heartRate"
                      type="number"
                      value={formData.heartRate}
                      onChange={handleInputChange}
                      error={!!formErrors.heartRate}
                      helperText={formErrors.heartRate}
                      InputProps={{ 
                        inputProps: { min: 30, max: 220 },
                        endAdornment: <Typography variant="caption">lpm</Typography>
                      }}
                    />
                  </Box>
                  <Tooltip title="Valor normal: 60-100 lpm">
                    <InfoIcon color="action" sx={{ ml: 1, mt: 3 }} />
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <FieldLabel>
                      Saturación de Oxígeno
                    </FieldLabel>
                    <StyledTextField
                      fullWidth
                      placeholder="%"
                      name="oxygenSaturation"
                      type="number"
                      value={formData.oxygenSaturation}
                      onChange={handleInputChange}
                      error={!!formErrors.oxygenSaturation}
                      helperText={formErrors.oxygenSaturation}
                      InputProps={{ 
                        inputProps: { min: 50, max: 100, step: "1" },
                        endAdornment: <Typography variant="caption">%</Typography>
                      }}
                    />
                  </Box>
                  <Tooltip title="Valor normal: 95-100%">
                    <InfoIcon color="action" sx={{ ml: 1, mt: 3 }} />
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <FieldLabel>
                      Temperatura
                    </FieldLabel>
                    <StyledTextField
                      fullWidth
                      placeholder="°C"
                      name="temperature"
                      type="number"
                      value={formData.temperature}
                      onChange={handleInputChange}
                      error={!!formErrors.temperature}
                      helperText={formErrors.temperature}
                      InputProps={{ 
                        inputProps: { min: 30, max: 45, step: "0.1" },
                        endAdornment: <Typography variant="caption">°C</Typography>
                      }}
                    />
                  </Box>
                  <Tooltip title="Valor normal: 36.1-37.2°C">
                    <InfoIcon color="action" sx={{ ml: 1, mt: 3 }} />
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <FieldLabel>
                      Glucosa en Sangre
                    </FieldLabel>
                    <StyledTextField
                      fullWidth
                      placeholder="mg/dL"
                      name="bloodGlucose"
                      type="number"
                      value={formData.bloodGlucose}
                      onChange={handleInputChange}
                      error={!!formErrors.bloodGlucose}
                      helperText={formErrors.bloodGlucose}
                      InputProps={{ 
                        inputProps: { min: 20, max: 600, step: "1" },
                        endAdornment: <Typography variant="caption">mg/dL</Typography>
                      }}
                    />
                  </Box>
                  <Tooltip title="Normal en ayuno: 70-99 mg/dL">
                    <InfoIcon color="action" sx={{ ml: 1, mt: 3 }} />
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <FieldLabel>
                      Circunferencia de Cintura
                    </FieldLabel>
                    <StyledTextField
                      fullWidth
                      placeholder="cm"
                      name="waistCircumference"
                      type="number"
                      value={formData.waistCircumference}
                      onChange={handleInputChange}
                      error={!!formErrors.waistCircumference}
                      helperText={formErrors.waistCircumference}
                      InputProps={{ 
                        inputProps: { min: 40, max: 200 },
                        endAdornment: <Typography variant="caption">cm</Typography>
                      }}
                    />
                  </Box>
                  <Tooltip title="Riesgo elevado: >94cm (hombres), >80cm (mujeres)">
                    <InfoIcon color="action" sx={{ ml: 1, mt: 3 }} />
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.08)' }} />
            
            <SectionTitle variant="h6">
              Hábitos y Factores de Riesgo
            </SectionTitle>
            
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FieldLabel>
                    Tabaquismo
                  </FieldLabel>
                  <RadioGroup
                    row
                    name="smoking"
                    value={formData.smoking !== undefined ? formData.smoking.toString() : "false"}
                    onChange={(e) => setFormData({
                      ...formData,
                      smoking: e.target.value === "true"
                    })}
                  >
                    <FormControlLabel value="true" control={<StyledRadio />} label="Sí" />
                    <FormControlLabel value="false" control={<StyledRadio />} label="No" />
                  </RadioGroup>
                  {formData.smoking && (
                    <StyledFormHelperText>
                      El paciente es fumador - Factor de riesgo cardiovascular
                    </StyledFormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FieldLabel>
                    Consumo de Alcohol
                  </FieldLabel>
                  <RadioGroup
                    row
                    name="alcohol"
                    value={formData.alcohol !== undefined ? formData.alcohol.toString() : "false"}
                    onChange={(e) => setFormData({
                      ...formData,
                      alcohol: e.target.value === "true"
                    })}
                  >
                    <FormControlLabel value="true" control={<StyledRadio />} label="Sí" />
                    <FormControlLabel value="false" control={<StyledRadio />} label="No" />
                  </RadioGroup>
                  {formData.alcohol && (
                    <StyledFormHelperText>
                      El paciente consume alcohol regularmente
                    </StyledFormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={formData.cancer}
                      onChange={handleInputChange}
                      name="cancer"
                    />
                  }
                  label="El paciente tiene o ha tenido cáncer"
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.08)' }} />
            
            <Grid container spacing={3}>
              {/* Patologías presentes */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
                  Patologías Presentes
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <FieldLabel>
                    Agregar patología
                  </FieldLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="Escriba y presione Enter"
                    id="currentPathology"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (handleArrayInput('currentPathologies', e.target.value)) {
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <StyledFormHelperText sx={{ mx: 1 }}>
                    Presiona Enter para agregar cada patología
                  </StyledFormHelperText>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.currentPathologies.map((pathology, index) => (
                    <StyledChip
                      key={index}
                      label={pathology}
                      onDelete={() => handleRemoveItem('currentPathologies', index)}
                    />
                  ))}
                </Box>
              </Grid>
              
              {/* Enfermedades crónicas */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
                  Enfermedades Crónicas
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <FieldLabel>
                    Agregar enfermedad crónica
                  </FieldLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="Escriba y presione Enter"
                    id="chronicDisease"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (handleArrayInput('chronicDiseases', e.target.value)) {
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <StyledFormHelperText sx={{ mx: 1 }}>
                    Presiona Enter para agregar cada enfermedad
                  </StyledFormHelperText>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.chronicDiseases.map((disease, index) => (
                    <StyledChip
                      key={index}
                      label={disease}
                      onDelete={() => handleRemoveItem('chronicDiseases', index)}
                    />
                  ))}
                </Box>
              </Grid>
              
              {/* Alergias */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
                  Alergias
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <FieldLabel>
                    Agregar alergia
                  </FieldLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="Escriba y presione Enter"
                    id="allergy"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (handleArrayInput('allergies', e.target.value)) {
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <StyledFormHelperText sx={{ mx: 1 }}>
                    Presiona Enter para agregar cada alergia
                  </StyledFormHelperText>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.allergies.map((allergy, index) => (
                    <StyledChip
                      key={index}
                      label={allergy}
                      onDelete={() => handleRemoveItem('allergies', index)}
                    />
                  ))}
                </Box>
              </Grid>
              
              {/* Medicamentos */}
              <Grid item xs={12} md={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
                  Medicamentos
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <FieldLabel>
                    Agregar medicamento
                  </FieldLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="Escriba y presione Enter"
                    id="medication"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (handleArrayInput('medications', e.target.value)) {
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <StyledFormHelperText sx={{ mx: 1 }}>
                    Presiona Enter para agregar cada medicamento
                  </StyledFormHelperText>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.medications.map((medication, index) => (
                    <StyledChip
                      key={index}
                      label={medication}
                      onDelete={() => handleRemoveItem('medications', index)}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4 }}>
              <FieldLabel>
                Notas Médicas
              </FieldLabel>
              <StyledTextField
                fullWidth
                multiline
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Notas privadas sobre el paciente (solo visibles para profesionales de la salud y la IA)"
              />
              <StyledFormHelperText sx={{ mx: 1, mt: 1 }}>
                Estas notas solo serán visibles para los profesionales de la salud y la IA, nunca para el paciente.
              </StyledFormHelperText>
            </Box>
          </form>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <StyledButton
          onClick={onClose}
          disabled={saving}
          variant="text"
        >
          Cancelar
        </StyledButton>
        <StyledButton
          type="submit"
          form="patient-form"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving || loading}
        >
          {saving ? 'Guardando...' : 'Guardar Paciente'}
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default PatientModal;